import React, { useMemo } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FieldRow from "./FieldRow";
import { suggestKey } from "./formAdminConstants";

/**
 * 입력칸 목록 편집.
 *
 * 슬롯 중복은 막지 않고 <b>보여준다</b>. 서버도 같은 태도다(앞의 칸이 이긴다) — 편집 중간에는
 * 슬롯을 옮기려고 잠깐 둘에 붙는 상태가 자연스럽게 생기는데, 그걸 막으면 옮길 방법이 없어진다.
 */
export default function FieldsEditor({ fields, slots, newKeys, onChange }) {
    // 슬롯 → 그 슬롯을 먼저 차지한 칸 이름. 중복 경고를 각 줄에 내려주기 위한 것.
    const slotOwner = useMemo(() => {
        const map = {};
        fields.forEach((f) => {
            // 키로 잡아 둔다 — 라벨로 잡으면 아래에서 key 와 비교할 때 항상 어긋나 자기 자신을 중복으로 신고한다.
            if (f.slot && !map[f.slot]) map[f.slot] = { key: f.key, label: f.label || f.key };
        });
        return map;
    }, [fields]);

    const setAt   = (i, next) => onChange(fields.map((f, j) => (j === i ? next : f)));
    const remove  = (i)       => onChange(fields.filter((_, j) => j !== i));
    const move    = (from, to) => {
        if (to < 0 || to >= fields.length) return;
        const next = [...fields];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChange(next);
    };
    const add = () => {
        const key = suggestKey("", fields.map((f) => f.key));
        onChange([...fields, { key, label: "", type: "TEXT", required: false }]);
        newKeys.add(key);
    };

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                    칸 {fields.length}개 · 슬롯 {Object.keys(slotOwner).length}개 연결됨
                </Typography>
                <Button size="small" startIcon={<AddRoundedIcon />} onClick={add}
                        sx={{ fontSize: 12, fontWeight: 700 }}>
                    칸 추가
                </Button>
            </Stack>

            <Stack direction="row" spacing={0.75} sx={{ px: 1, pb: 0.5 }}>
                <Box sx={{ width: 26, flexShrink: 0 }} />
                <HeadLabel sx={{ flex: 1, minWidth: 120 }}>칸 이름</HeadLabel>
                <HeadLabel sx={{ width: 132 }}>종류</HeadLabel>
                <HeadLabel sx={{ width: 168 }}>연동 슬롯</HeadLabel>
                <HeadLabel sx={{ width: 42, textAlign: "center" }}>필수</HeadLabel>
                <Box sx={{ width: 62, flexShrink: 0 }} />
            </Stack>

            {fields.map((f, i) => (
                <FieldRow
                    key={`${f.key}-${i}`}
                    field={f}
                    index={i}
                    total={fields.length}
                    slots={slots}
                    slotOwner={f.slot ? slotOwner[f.slot] : undefined}
                    isNew={newKeys.has(f.key)}
                    onChange={(next) => setAt(i, next)}
                    onMove={move}
                    onRemove={remove}
                />
            ))}

            {fields.length === 0 && (
                <Box sx={{ py: 5, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    칸이 없습니다. «칸 추가» 로 시작하세요.
                </Box>
            )}
        </Box>
    );
}

function HeadLabel({ children, sx }) {
    return (
        <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#94a3b8",
                          letterSpacing: 0.3, flexShrink: 0, ...sx }}>
            {children}
        </Typography>
    );
}
